import { CSSProperties } from 'react';
import {
  UI_ATLAS_CELL,
  UI_ATLAS_HEIGHT,
  UI_ATLAS_IMAGE,
  UI_ATLAS_WIDTH,
  UI_BAR_FRAMES,
} from '../../data/ui.atlas';
import { useNineSliceSource } from '../../hooks/useNineSliceSource';

const FILL_COLOR = '#bbc3d0';
const FILL_HEIGHT_PX = 12;
const FILL_INSET_PX = 6;
const ARROW_GAP_PX = 10;

interface Props {
  value: number;
  scale?: number;
  className?: string;
}

const atlasBg = (frameKey: string, scale: number): CSSProperties => {
  const frame = UI_BAR_FRAMES[frameKey].frame;
  return {
    backgroundImage: `url('${UI_ATLAS_IMAGE}')`,
    backgroundPosition: `-${frame.x * scale}px -${frame.y * scale}px`,
    backgroundSize: `${UI_ATLAS_WIDTH * scale}px ${UI_ATLAS_HEIGHT * scale}px`,
    imageRendering: 'pixelated' as CSSProperties['imageRendering'],
  };
};

export const TrackBar = ({ value, scale = 3, className }: Props) => {
  const clamped = Math.max(0, Math.min(1, value));
  const tileSize = UI_ATLAS_CELL * scale;

  // Center tile extracted to a canvas so it can be stretched in X with
  // `background-size: 100% 100%` — not possible directly from the atlas, where
  // the cell position depends on the PNG's natural size.
  const midFrame = UI_BAR_FRAMES['bar-empty-mid'].frame;
  const midSrc = useNineSliceSource(
    UI_ATLAS_IMAGE,
    midFrame.x,
    midFrame.y,
    UI_ATLAS_CELL,
  );

  return (
    <span
      className={`trackbar${className ? ` ${className}` : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: ARROW_GAP_PX,
      }}
    >
      <span
        style={{
          width: tileSize,
          height: tileSize,
          flexShrink: 0,
          ...atlasBg('bar-arrow-left', scale),
        }}
      />

      <span
        style={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          height: tileSize,
        }}
      >
        <span
          style={{
            width: tileSize,
            height: tileSize,
            flexShrink: 0,
            ...atlasBg('bar-filled-start', scale),
          }}
        />
        <span
          style={{
            flex: 1,
            height: tileSize,
            backgroundImage: midSrc ? `url('${midSrc}')` : undefined,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            imageRendering: 'pixelated',
          }}
        />
        <span
          style={{
            width: tileSize,
            height: tileSize,
            flexShrink: 0,
            ...atlasBg('bar-empty-end', scale),
          }}
        />

        <span
          style={{
            position: 'absolute',
            left: FILL_INSET_PX,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `calc((100% - ${FILL_INSET_PX}px) * ${clamped})`,
            height: FILL_HEIGHT_PX,
            background: FILL_COLOR,
            pointerEvents: 'none',
          }}
        />

        <span
          style={{
            position: 'absolute',
            left: `calc(${FILL_INSET_PX}px + (100% - ${FILL_INSET_PX}px) * ${clamped})`,
            top: '50%',
            width: tileSize,
            height: tileSize,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            ...atlasBg('bar-track', scale),
          }}
        />
      </span>

      <span
        style={{
          width: tileSize,
          height: tileSize,
          flexShrink: 0,
          ...atlasBg('bar-arrow-right', scale),
        }}
      />
    </span>
  );
};
